import { useQuery } from '@tanstack/react-query'
import { CalendarDays } from 'lucide-react'
import { MUSIC_HOW, achieveApi } from '@/lib/achieve'
import { decorLabel, wearing, type DecorEntry, type DecorKind, type Look } from '@/lib/decor'
import { useAppStore } from '@/store/app'
import { AchievementIcon } from '../AchievementIcon'
import { PlusMark } from '../PlusMark'
import { Modal } from './Modal'
import { DecorPreview } from './DecorPreview'
import layer from './SettingsLayer.module.css'
import styles from './LockedDecorModal.module.css'

const NOUN: Record<DecorKind, string> = { frame: 'рамка', effect: 'эффект', plate: 'табличка' }

const upperFirst = (s: string) => s.slice(0, 1).toLocaleUpperCase('ru') + s.slice(1)

/**
 * Закрытое украшение: откуда оно берётся, что для этого сделать и как оно будет выглядеть на вас.
 * Открывается нажатием на закрытую плитку магазина; base — то, что сейчас выбрано в магазине.
 */
export function LockedDecorModal({ kind, entry, base, onClose }: { kind: DecorKind; entry: DecorEntry; base: Look; onClose: () => void }) {
  const setView = useAppStore((s) => s.setView)
  const closeSettings = useAppStore((s) => s.closeSettings)
  const openSettings = useAppStore((s) => s.openSettings)
  const showContent = useAppStore((s) => s.showContent)
  const a = entry.achievement
  // описание достижения есть только в его списке; пока он грузится, окно показывает имя и прогресс
  const { data: achievements } = useQuery({ queryKey: ['achievements'], queryFn: achieveApi.list, enabled: Boolean(a) })
  const description = a ? achievements?.find((x) => x.id === a.id)?.description : undefined
  const noun = NOUN[kind]
  const counted = a?.goal !== undefined && a.goal > 1 && a.progress !== undefined
  const pct = counted ? Math.min(100, Math.round((a.progress! / a.goal!) * 100)) : 0

  return (
    <Modal title={decorLabel(kind, entry.name)} onClose={onClose} width={420}>
      <div className={styles.body}>
        {a ? (
          <>
            <div className={styles.source}>
              <AchievementIcon id={a.id} size={48} />
              <span className={styles.sourceText}>
                <span className={styles.sourceName}>Достижение «{a.name}»</span>
                {description ? <span className={styles.sourceDesc}>{description}</span> : null}
              </span>
            </div>
            {counted ? (
              <div className={styles.progress}>
                <span className={styles.count}>{a.progress} из {a.goal} {a.unit}</span>
                <span className={styles.bar}><span className={styles.barValue} style={{ width: `${pct}%` }} /></span>
              </div>
            ) : null}
            {a.id === 'dj_50' ? <p className={styles.hint}>{MUSIC_HOW}</p> : null}
            <p className={styles.lead}>Получите достижение, и {noun} останется у вас навсегда.</p>
          </>
        ) : entry.plus ? (
          <div className={styles.source}>
            <PlusMark size={48} label={null} />
            <span className={styles.sourceText}>
              <span className={styles.sourceName}>Кличат Плюс</span>
              <span className={styles.sourceDesc}>Носить можно, пока действует Плюс.</span>
            </span>
          </div>
        ) : entry.season ? (
          <>
            <div className={styles.source}>
              <span className={styles.sourceIcon}><CalendarDays size={24} aria-hidden="true" /></span>
              <span className={styles.sourceText}>
                <span className={styles.sourceName}>Сезон «{entry.season.name}»</span>
                <span className={styles.sourceDesc}>{upperFirst(entry.season.dates)}</span>
              </span>
            </div>
            <p className={styles.lead}>Если надеть в эти дни, {noun} останется у вас навсегда.</p>
          </>
        ) : (
          <p className={styles.lead}>Пока не выдаётся.</p>
        )}

        <div className={styles.preview}>
          <DecorPreview look={wearing(base, [`${kind}:${entry.id}`])} />
        </div>

        {a ? (
          <button
            type="button"
            className={layer.button}
            onClick={() => {
              onClose()
              closeSettings()
              setView({ kind: 'home', page: 'achievements' })
              showContent() // на телефоне — сразу страница, а не список бесед
            }}
          >
            Все достижения
          </button>
        ) : entry.plus ? (
          <button
            type="button"
            className={layer.button}
            onClick={() => {
              onClose()
              openSettings('premium')
            }}
          >
            Как получить Плюс
          </button>
        ) : null}
      </div>
    </Modal>
  )
}
